// Resolves the 5 verification badges from a user/provider-shaped object.
// Works for both the private `user` object (AuthContext) and the public
// `provider` object (getPublicProfile / getTutorsAndAcademies) — both carry
// the same field names, just with different levels of detail.
//
// 2 badges are AUTOMATIC — computed here from existing fields, no request/
// review flow. 3 are REQUESTABLE — read from `entity.badges[]`, populated via
// the account page's badge request flow and approved by an admin.

export const BADGE_TYPES = {
  IDENTITY_VERIFIED: "identity_verified",
  PHONE_EMAIL_VERIFIED: "phone_email_verified",
  QUALIFICATION_VERIFIED: "qualification_verified",
  PROFESSIONAL_CERTIFICATE_VERIFIED: "professional_certificate_verified",
  TRAINING_CENTRE_REGISTRATION_VERIFIED: "training_centre_registration_verified",
};

export const REQUESTABLE_BADGE_TYPES = [
  BADGE_TYPES.QUALIFICATION_VERIFIED,
  BADGE_TYPES.PROFESSIONAL_CERTIFICATE_VERIFIED,
  BADGE_TYPES.TRAINING_CENTRE_REGISTRATION_VERIFIED,
];

// `shortLabel` is what renders on compact cards (search results) — a
// learner comparing four tutors should see "ID Verified" at a glance, not
// just an unlabelled icon they have to hover to decode (which doesn't even
// work on a touchscreen).
export const BADGE_META = {
  [BADGE_TYPES.IDENTITY_VERIFIED]: {
    label: "Identity Verified",
    shortLabel: "ID Verified",
    icon: "🪪",
    automatic: true,
  },
  [BADGE_TYPES.PHONE_EMAIL_VERIFIED]: {
    label: "Phone/Email Verified",
    shortLabel: "Verified",
    icon: "✅",
    automatic: true,
  },
  [BADGE_TYPES.QUALIFICATION_VERIFIED]: {
    label: "Qualification Verified",
    shortLabel: "Qualified",
    icon: "🎓",
    automatic: false,
  },
  [BADGE_TYPES.PROFESSIONAL_CERTIFICATE_VERIFIED]: {
    label: "Professional Certificate Verified",
    shortLabel: "Certified",
    icon: "📜",
    automatic: false,
  },
  [BADGE_TYPES.TRAINING_CENTRE_REGISTRATION_VERIFIED]: {
    label: "Training Centre Registration Verified",
    shortLabel: "Registered",
    icon: "🏫",
    automatic: false,
    roleRequired: "institution",
  },
};

/**
 * Returns all badges applicable to this entity's role, each with a resolved
 * status: "none" | "awaiting" | "approved" | "rejected".
 */
export function getUserBadges(entity) {
  if (!entity) return [];

  const requestableStatus = {};
  (entity.badges || []).forEach((b) => {
    requestableStatus[b.type] = b.status;
  });

  // Identity is considered verified via EITHER an admin-approved KYC review
  // OR a matched NIN lookup — either is sufficient proof of identity, so
  // they resolve to the same single badge rather than two separate ones.
  const ninStatus =
    entity.ninVerificationStatus || entity.ninVerification?.status || "none";
  const identityStatus =
    entity.userApprovalStatus === "approved" ||
    ["found", "verified"].includes(ninStatus)
      ? "approved"
      : "none";

  const phoneEmailStatus =
    entity.emailVerified || entity.phoneVerified ? "approved" : "none";

  const isInstitution = entity.role === "institution";

  const badges = [
    {
      type: BADGE_TYPES.IDENTITY_VERIFIED,
      ...BADGE_META[BADGE_TYPES.IDENTITY_VERIFIED],
      status: identityStatus,
    },
    {
      type: BADGE_TYPES.PHONE_EMAIL_VERIFIED,
      ...BADGE_META[BADGE_TYPES.PHONE_EMAIL_VERIFIED],
      status: phoneEmailStatus,
    },
    {
      type: BADGE_TYPES.QUALIFICATION_VERIFIED,
      ...BADGE_META[BADGE_TYPES.QUALIFICATION_VERIFIED],
      status: requestableStatus[BADGE_TYPES.QUALIFICATION_VERIFIED] || "none",
    },
    {
      type: BADGE_TYPES.PROFESSIONAL_CERTIFICATE_VERIFIED,
      ...BADGE_META[BADGE_TYPES.PROFESSIONAL_CERTIFICATE_VERIFIED],
      status:
        requestableStatus[BADGE_TYPES.PROFESSIONAL_CERTIFICATE_VERIFIED] ||
        "none",
    },
  ];

  if (isInstitution) {
    badges.push({
      type: BADGE_TYPES.TRAINING_CENTRE_REGISTRATION_VERIFIED,
      ...BADGE_META[BADGE_TYPES.TRAINING_CENTRE_REGISTRATION_VERIFIED],
      status:
        requestableStatus[BADGE_TYPES.TRAINING_CENTRE_REGISTRATION_VERIFIED] ||
        "none",
    });
  }

  return badges;
}

export function getEarnedBadges(entity) {
  return getUserBadges(entity).filter((b) => b.status === "approved");
}
